import { useEffect, useState } from "react";
import { X, DownloadSimple, Export, PlusSquare } from "@phosphor-icons/react";

const DISMISS_KEY = "rv_install_dismissed_at";
const SNOOZE_MS = 14 * 24 * 3600 * 1000;
const VISIT_DELAY_MS = 30000;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

// Mobile "Add to Home Screen" banner: one-tap install on Android/Chrome, Share-sheet steps on iOS Safari.
export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (isStandalone() || snoozed() || !window.matchMedia("(pointer: coarse)").matches) return undefined;
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    const t = setTimeout(() => setVisible(true), VISIT_DELAY_MS);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
    dismiss();
  };

  if (!visible || (!deferred && !ios)) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:w-[380px] z-[90] rv-fade">
      <div className="flex items-center gap-4 rounded-2xl border border-amber/30 bg-[#0A0A0A]/95 backdrop-blur-xl p-4 shadow-2xl shadow-black/60">
        <img src="/icon-192.png" alt="ReelVault" className="h-11 w-11 shrink-0 rounded-xl border border-amber/30" />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-[#EDEDED] leading-tight">Install ReelVault</p>
          {ios ? (
            <p data-testid="pwa-ios-steps" className="mt-1 flex flex-wrap items-center gap-1 font-mono text-[10px] uppercase tracking-[0.1em] text-white/55">
              Tap <Export size={13} weight="bold" className="text-amber" /> Share, then
              <PlusSquare size={13} weight="bold" className="text-amber" /> Add to Home Screen
            </p>
          ) : (
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/40">
              One-tap access, right on your home screen
            </p>
          )}
        </div>
        {!ios && (
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="flex items-center gap-1.5 rounded-full bg-amber px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-black font-bold hover:bg-amber/80 transition-colors duration-300"
          >
            <DownloadSimple size={14} weight="bold" /> Install
          </button>
        )}
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-white/40 hover:text-white transition-colors duration-300"
        >
          <X size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
